import { useState } from 'react';
import {
  CalendarOutlined,
  BarsOutlined,
  SettingOutlined,
  EnvironmentOutlined,
  LineChartOutlined,
  AreaChartOutlined,
  InfoCircleOutlined,
  CloudOutlined,
  AimOutlined,
  ReloadOutlined,
  LinkOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { Button, Modal, Input, message } from 'antd';
import { useTheme } from '../../contexts/useTheme';
import { type EventInfo } from '../../api/infosService';
import { type StationData } from '../../api/stationObservationsService';
import { type ForecastRain } from '../../api/forecastService';
import { isTioProvider, type MapTileProvider } from './useNowcastingState';
import dayjs from 'dayjs';

interface RadarTile {
  timestamp: string;
  url: string;
  datetime: dayjs.Dayjs;
  isNowcast?: boolean;
  forecastOffset?: number;
}

interface MapToolbarProps {
  observationsEnabled: boolean;
  isScheduleVisible: boolean;
  setIsScheduleVisible: (visible: boolean) => void;
  isLegendVisible: boolean;
  setIsLegendVisible: (visible: boolean) => void;
  isRainOnlyMode: boolean;
  toggleRainOnlyMode: () => void;
  isPiPVisible: boolean;
  setIsPiPVisible: (visible: boolean) => void;
  isSplitViewEnabled: boolean;
  setIsSplitViewEnabled: (enabled: boolean) => void;
  isStationsVisible: boolean;
  setIsStationsVisible: (visible: boolean) => void;
  isStationsComparisonVisible: boolean;
  setIsStationsComparisonVisible: (visible: boolean) => void;
  isServiceInfoVisible: boolean;
  setIsServiceInfoVisible: (visible: boolean) => void;
  isRainForecastVisible: boolean;
  setIsRainForecastVisible: (visible: boolean) => void;
  isTioForecastVisible: boolean;
  setIsTioForecastVisible: (visible: boolean) => void;
  isMinutelyForecastVisible: boolean;
  setIsMinutelyForecastVisible: (visible: boolean) => void;
  hasMinutelyForecasts: boolean;
  isCircleOverlayVisible: boolean;
  setIsCircleOverlayVisible: (visible: boolean) => void;
  showRadarBbox: boolean;
  setShowRadarBbox: (visible: boolean) => void;
  isMapSettingsVisible: boolean;
  setIsMapSettingsVisible: (visible: boolean) => void;
  mapTileProvider: MapTileProvider;
  tiles: RadarTile[];
  stations: StationData[];
  selectedDateRange: { start: Date; end: Date } | null;
  filteredInfos: EventInfo[];
  currentInfo: EventInfo | undefined;
  rainForecasts: ForecastRain[];
  tioTimestamps: string[];
  handleCenterOnCircuit: () => void;
  handleResetMapView: () => void;
  handleResetCardPositions: () => void;
  /** Root-only: show current tile URL for debugging */
  isRoot?: boolean;
  currentTileUrl?: string | null;
  /** Open map in fullscreen view */
  onOpenFullScreen?: () => void;
  /** Whether map is currently in fullscreen (shows exit icon when true) */
  isMapFullScreen?: boolean;
  /** Exit fullscreen view */
  onCloseFullScreen?: () => void;
}

export const MapToolbar = ({
  observationsEnabled,
  isScheduleVisible,
  setIsScheduleVisible,
  isLegendVisible,
  setIsLegendVisible,
  isRainOnlyMode,
  toggleRainOnlyMode,
  isPiPVisible,
  setIsPiPVisible,
  isSplitViewEnabled,
  setIsSplitViewEnabled,
  isStationsVisible,
  setIsStationsVisible,
  isStationsComparisonVisible,
  setIsStationsComparisonVisible,
  isServiceInfoVisible,
  setIsServiceInfoVisible,
  isRainForecastVisible,
  setIsRainForecastVisible,
  isTioForecastVisible,
  setIsTioForecastVisible,
  isMinutelyForecastVisible,
  setIsMinutelyForecastVisible,
  hasMinutelyForecasts,
  isCircleOverlayVisible,
  setIsCircleOverlayVisible,
  showRadarBbox,
  setShowRadarBbox,
  isMapSettingsVisible,
  setIsMapSettingsVisible,
  mapTileProvider,
  tiles,
  stations,
  selectedDateRange,
  filteredInfos,
  currentInfo,
  rainForecasts,
  tioTimestamps,
  handleCenterOnCircuit,
  handleResetMapView,
  handleResetCardPositions,
  isRoot,
  currentTileUrl,
  onOpenFullScreen,
  isMapFullScreen,
  onCloseFullScreen,
}: MapToolbarProps) => {
  const { theme } = useTheme();
  const [isTileUrlModalOpen, setIsTileUrlModalOpen] = useState(false);

  const handleShowTileUrl = () => {
    setIsTileUrlModalOpen(true);
  };

  const handleCopyTileUrl = async () => {
    if (currentTileUrl) {
      try {
        await navigator.clipboard.writeText(currentTileUrl);
        message.success('URL copied to clipboard');
      } catch {
        message.error('Failed to copy URL');
      }
    }
  };

  return (
    <div style={{
      position: 'absolute',
      top: 110, // Position below Mapbox navigation control (which is ~100px tall)
      right: 10,
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    }}>
      {/* First group - Info panels */}
      <div className="mapboxgl-ctrl mapboxgl-ctrl-group" style={{
        backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fff',
        boxShadow: theme === 'dark' ? '0 0 0 2px rgba(255,255,255,.1)' : '0 0 0 2px rgba(0,0,0,.1)',
        borderRadius: 4,
      }}>
        {observationsEnabled && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={() => setIsScheduleVisible(!isScheduleVisible)}
            title={isScheduleVisible ? "Hide Schedule" : "Show Schedule"}
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: isScheduleVisible
                ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                : 'transparent',
              cursor: 'pointer',
              borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)',
            }}
          >
            <CalendarOutlined style={{ fontSize: '15px', color: isScheduleVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
          </button>
        )}
        <button
          type="button"
          className="mapboxgl-ctrl-icon"
          onClick={() => setIsLegendVisible(!isLegendVisible)}
          title={isLegendVisible ? "Hide Legend" : "Show Legend"}
          style={{
            width: '29px',
            height: '29px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            backgroundColor: isLegendVisible
              ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
              : 'transparent',
            cursor: 'pointer',
            borderBottom: (isTioProvider(mapTileProvider)) || tiles.length > 0 || (stations.length > 0 && selectedDateRange) || (filteredInfos.length > 0 && currentInfo) || (rainForecasts.length > 0 && selectedDateRange)
              ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)')
              : 'none',
          }}
        >
          <BarsOutlined style={{ fontSize: '15px', color: isLegendVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
        </button>
        {/* Rain Only Mode Toggle - only show for Tomorrow.io tiles */}
        {isTioProvider(mapTileProvider) && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={toggleRainOnlyMode}
            title={isRainOnlyMode ? "Show All Precipitation Types" : "Show Rain Only"}
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: isRainOnlyMode
                ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                : 'transparent',
              cursor: 'pointer',
              borderBottom: tiles.length > 0 || (stations.length > 0 && selectedDateRange) || (filteredInfos.length > 0 && currentInfo) || (rainForecasts.length > 0 && selectedDateRange) || tioTimestamps.length > 0
                ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)')
                : 'none',
            }}
          >
            {/* Rain drop icon */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2C12 2 6 10 6 14C6 17.3137 8.68629 20 12 20C15.3137 20 18 17.3137 18 14C18 10 12 2 12 2Z"
                fill={isRainOnlyMode ? '#1890ff' : 'transparent'}
                stroke={isRainOnlyMode ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333')}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {/* PiP and Split view - always available so user can toggle view even when no tile data */}
        <>
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={() => setIsPiPVisible(!isPiPVisible)}
            title={isPiPVisible ? "Hide Circuit View" : "Show Circuit View"}
              style={{
                width: '29px',
                height: '29px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: 'none',
                backgroundColor: isPiPVisible
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                  : 'transparent',
                cursor: 'pointer',
                borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="20" height="20" rx="2" stroke={isPiPVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333')} strokeWidth="2" />
                <rect x="13" y="13" width="8" height="8" rx="1" fill={isPiPVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333')} />
              </svg>
            </button>
            <button
              type="button"
              className="mapboxgl-ctrl-icon"
              onClick={() => setIsSplitViewEnabled(!isSplitViewEnabled)}
              title={isSplitViewEnabled ? "Disable Split View" : "Enable Split View"}
              style={{
                width: '29px',
                height: '29px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: 'none',
                backgroundColor: isSplitViewEnabled
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                  : 'transparent',
                cursor: 'pointer',
                borderBottom: (stations.length > 0 && selectedDateRange) || (filteredInfos.length > 0 && currentInfo) || (rainForecasts.length > 0 && selectedDateRange)
                  ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)')
                  : 'none',
              }}
            >
              {/* Split view icon - two rectangles side by side */}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="3" width="8" height="18" rx="1" stroke={isSplitViewEnabled ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333')} strokeWidth="2" fill={isSplitViewEnabled ? '#1890ff' : 'none'} fillOpacity="0.2" />
                <rect x="14" y="3" width="8" height="18" rx="1" stroke={isSplitViewEnabled ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333')} strokeWidth="2" fill={isSplitViewEnabled ? '#1890ff' : 'none'} fillOpacity="0.2" />
              </svg>
            </button>
        </>
        {stations.length > 0 && selectedDateRange && (
          <>
            <button
              type="button"
              className="mapboxgl-ctrl-icon"
              onClick={() => setIsStationsVisible(!isStationsVisible)}
              title={isStationsVisible ? "Hide Stations" : "Show Stations"}
              style={{
                width: '29px',
                height: '29px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: 'none',
                backgroundColor: isStationsVisible
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                  : 'transparent',
                cursor: 'pointer',
                borderBottom: (isTioProvider(mapTileProvider) && tioTimestamps.length > 0) || (filteredInfos.length > 0 && currentInfo) || (rainForecasts.length > 0 && selectedDateRange)
                  ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)')
                  : 'none',
              }}
            >
              <LineChartOutlined style={{ fontSize: '15px', color: isStationsVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
            </button>
            <button
              type="button"
              className="mapboxgl-ctrl-icon"
              onClick={() => setIsStationsComparisonVisible(!isStationsComparisonVisible)}
              title={isStationsComparisonVisible ? "Hide Stations Comparison" : "Show Stations Comparison"}
              style={{
                width: '29px',
                height: '29px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: 'none',
                backgroundColor: isStationsComparisonVisible
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                  : 'transparent',
                cursor: 'pointer',
                borderBottom: (isTioProvider(mapTileProvider) && tioTimestamps.length > 0) || (filteredInfos.length > 0 && currentInfo) || (rainForecasts.length > 0 && selectedDateRange)
                  ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)')
                  : 'none',
              }}
            >
              <AreaChartOutlined style={{ fontSize: '15px', color: isStationsComparisonVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
            </button>
          </>
        )}
        {/* T.io Forecast Panel - only when on tomorrow.io with timestamps */}
        {isTioProvider(mapTileProvider) && tioTimestamps.length > 0 && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={() => setIsTioForecastVisible(!isTioForecastVisible)}
            title={isTioForecastVisible ? "Hide Forecast" : "Show Forecast"}
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: isTioForecastVisible
                ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                : 'transparent',
              cursor: 'pointer',
              borderBottom: (filteredInfos.length > 0 && currentInfo) || (rainForecasts.length > 0 && selectedDateRange)
                ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)')
                : 'none',
            }}
          >
            <CloudOutlined style={{ fontSize: '15px', color: isTioForecastVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
          </button>
        )}
        {isRoot && hasMinutelyForecasts && selectedDateRange && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={() => setIsMinutelyForecastVisible(!isMinutelyForecastVisible)}
            title={isMinutelyForecastVisible ? "Hide Minutely Nowcast" : "Show Minutely Nowcast"}
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: isMinutelyForecastVisible
                ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                : 'transparent',
              cursor: 'pointer',
              borderBottom: (filteredInfos.length > 0 && currentInfo) || (rainForecasts.length > 0 && selectedDateRange)
                ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)')
                : 'none',
            }}
          >
            <FieldTimeOutlined style={{ fontSize: '15px', color: isMinutelyForecastVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
          </button>
        )}
        {filteredInfos.length > 0 && currentInfo && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={() => setIsServiceInfoVisible(!isServiceInfoVisible)}
            title={isServiceInfoVisible ? "Hide Service Info" : "Show Service Info"}
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: isServiceInfoVisible
                ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                : 'transparent',
              cursor: 'pointer',
              borderBottom: (rainForecasts.length > 0 && selectedDateRange)
                ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)')
                : 'none',
            }}
          >
            <InfoCircleOutlined style={{ fontSize: '15px', color: isServiceInfoVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
          </button>
        )}
        {rainForecasts.length > 0 && selectedDateRange && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={() => setIsRainForecastVisible(!isRainForecastVisible)}
            title={isRainForecastVisible ? "Hide Rain Forecast" : "Show Rain Forecast"}
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: isRainForecastVisible
                ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                : 'transparent',
              cursor: 'pointer',
            }}
          >
            <CloudOutlined style={{ fontSize: '15px', color: isRainForecastVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
          </button>
        )}
      </div>

      {/* Map navigation - center and reset */}
      <div className="mapboxgl-ctrl mapboxgl-ctrl-group" style={{
        backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fff',
        boxShadow: theme === 'dark' ? '0 0 0 2px rgba(255,255,255,.1)' : '0 0 0 2px rgba(0,0,0,.1)',
        borderRadius: 4,
      }}>
        <button
          type="button"
          className="mapboxgl-ctrl-icon"
          onClick={handleCenterOnCircuit}
          title="Center on Circuit"
          style={{
            width: '29px',
            height: '29px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)',
          }}
        >
          <EnvironmentOutlined style={{ fontSize: '15px', color: theme === 'dark' ? '#fff' : '#333' }} />
        </button>
        <button
          type="button"
          className="mapboxgl-ctrl-icon"
          onClick={handleResetMapView}
          title="Reset Map View"
          style={{
            width: '29px',
            height: '29px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)',
          }}
        >
          <AimOutlined style={{ fontSize: '15px', color: theme === 'dark' ? '#fff' : '#333' }} />
        </button>
        <button
          type="button"
          className="mapboxgl-ctrl-icon"
          onClick={handleResetCardPositions}
          title="Reset Card Positions"
          style={{
            width: '29px',
            height: '29px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
          }}
        >
          <ReloadOutlined style={{ fontSize: '15px', color: theme === 'dark' ? '#fff' : '#333' }} />
        </button>
      </div>

      {/* Circuit overlay and map settings */}
      <div className="mapboxgl-ctrl mapboxgl-ctrl-group" style={{
        backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fff',
        boxShadow: theme === 'dark' ? '0 0 0 2px rgba(255,255,255,.1)' : '0 0 0 2px rgba(0,0,0,.1)',
        borderRadius: 4,
      }}>
        <button
          type="button"
          className="mapboxgl-ctrl-icon"
          onClick={() => setIsCircleOverlayVisible(!isCircleOverlayVisible)}
          title={isCircleOverlayVisible ? "Hide Circuit Circle" : "Show Circuit Circle"}
          style={{
            width: '29px',
            height: '29px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            backgroundColor: isCircleOverlayVisible
              ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
              : 'transparent',
            cursor: 'pointer',
            borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)',
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={isCircleOverlayVisible ? '#ff4d4f' : (theme === 'dark' ? '#fff' : '#333')}
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
          </svg>
        </button>
        {observationsEnabled && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={() => setShowRadarBbox(!showRadarBbox)}
            title={showRadarBbox ? "Hide Radar Bounding Box" : "Show Radar Bounding Box"}
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: showRadarBbox
                ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                : 'transparent',
              cursor: 'pointer',
              borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="6" width="16" height="12" rx="1" stroke={showRadarBbox ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333')} strokeWidth="2" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className="mapboxgl-ctrl-icon"
          onClick={() => setIsMapSettingsVisible(!isMapSettingsVisible)}
          title={isMapSettingsVisible ? "Hide Map Settings" : "Show Map Settings"}
          style={{
            width: '29px',
            height: '29px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            backgroundColor: isMapSettingsVisible
              ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
              : 'transparent',
            cursor: 'pointer',
            borderBottom: (isRoot || onOpenFullScreen || onCloseFullScreen) ? (theme === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)') : undefined,
          }}
        >
          <SettingOutlined style={{ fontSize: '15px', color: isMapSettingsVisible ? '#1890ff' : (theme === 'dark' ? '#fff' : '#333') }} />
        </button>
        {(onOpenFullScreen || onCloseFullScreen) && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={isMapFullScreen ? onCloseFullScreen : onOpenFullScreen}
            title={isMapFullScreen ? 'Exit full screen' : 'Full screen map'}
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: isMapFullScreen
                ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                : 'transparent',
              cursor: 'pointer',
            }}
          >
            {isMapFullScreen ? (
              <FullscreenExitOutlined style={{ fontSize: '15px', color: '#1890ff' }} />
            ) : (
              <FullscreenOutlined style={{ fontSize: '15px', color: theme === 'dark' ? '#fff' : '#333' }} />
            )}
          </button>
        )}
        {isRoot && (
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={handleShowTileUrl}
            title="Show current tile URL"
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <LinkOutlined style={{ fontSize: '15px', color: theme === 'dark' ? '#fff' : '#333' }} />
          </button>
        )}
      </div>

      <Modal
        title="Current Tile URL"
        open={isTileUrlModalOpen}
        onCancel={() => setIsTileUrlModalOpen(false)}
        footer={[
          <Button key="copy" onClick={handleCopyTileUrl} disabled={!currentTileUrl}>
            Copy URL
          </Button>,
          <Button key="close" onClick={() => setIsTileUrlModalOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {currentTileUrl ? (
          <Input.TextArea
            readOnly
            value={currentTileUrl}
            rows={6}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        ) : (
          <p style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.45)' }}>
            No tile is currently being displayed. Select a date range and ensure tiles are loaded.
          </p>
        )}
      </Modal>
    </div>
  );
};
